import {
  AnalyticsFilters,
  AnalyticsLookups,
  AnalyticsMetrics,
  AnalyticsPeriod
} from './procurement-analytics.types';

export const ANALYTICS_SORT_FIELDS = [
  'label',
  'soldQty',
  'returnedQty',
  'netQty',
  'shipmentCount',
  'revenue',
  'cogs',
  'profit',
  'marginPercent'
];

// Older browser/Node ICU versions know the same zone under its former IANA alias.
export const ANALYTICS_TIME_ZONE = (() => {
  try {
    new Intl.DateTimeFormat('en', { timeZone: 'Europe/Kyiv' });
    return 'Europe/Kyiv';
  } catch {
    return 'Europe/Kiev';
  }
})();

// Reuse ICU formatters across change detection; constructing one per table cell is expensive.
const numberFormatter = new Intl.NumberFormat('ru-UA', { maximumFractionDigits: 2 });
const timeFormatter = new Intl.DateTimeFormat('uk-UA', {
  timeZone: ANALYTICS_TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit'
});
const calendarFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: ANALYTICS_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
});

export function analyticsNumber(value: number | null | undefined): string {
  return value == null ? '—' : numberFormatter.format(value);
}

export function analyticsTime(value: string): string {
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? timeFormatter.format(date) : '—';
}

export function kyivDate(now = new Date()): string {
  const parts = calendarFormatter.formatToParts(now);
  const get = (type: string) => parts.find((part) => part.type === type)?.value || '';
  return [get('year'), get('month'), get('day')].join('-');
}

export function shiftDate(value: string, days: number): string {
  const date = new Date(value + 'T12:00:00Z');
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function validAnalyticsDate(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(value + 'T12:00:00Z');
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export function analyticsEventPeriodLabel(
  eventDate: string,
  period: AnalyticsPeriod,
  comparisonPeriod: AnalyticsPeriod
): string {
  const parsed = new Date(eventDate);
  const day = validAnalyticsDate(eventDate)
    ? eventDate
    : Number.isFinite(parsed.getTime())
    ? kyivDate(parsed)
    : '';
  const current = day >= period.dateFrom && day <= period.dateTo;
  const comparison = day >= comparisonPeriod.dateFrom && day <= comparisonPeriod.dateTo;
  if (current && comparison) return 'Текущий период и период сравнения';
  if (current) return 'Текущий период';
  if (comparison) return 'Период сравнения';
  return 'Вне выбранных периодов';
}

export function analyticsDefaults(now = new Date()): AnalyticsFilters {
  const today = kyivDate(now);
  return {
    granularity: 'auto',
    dateFrom: shiftDate(today, -90),
    dateTo: shiftDate(today, -1),
    comparison: 'previous',
    warehouseIds: [],
    brandIds: [],
    groupBy: 'product',
    noSales: false,
    noMinimumStock: false,
    coverageDays: 30,
    page: 1,
    pageSize: 25,
    sortBy: 'revenue',
    sortDirection: 'desc'
  };
}

function ids(value: unknown, allowZero = false): number[] {
  return [
    ...new Set(
      String(value ?? '')
        .split(',')
        .filter((item) => item.trim().length > 0)
        .map(Number)
    )
  ].filter((id) => Number.isInteger(id) && (allowZero ? id >= 0 : id > 0));
}

export function analyticsFiltersFromQuery(
  query: Record<string, unknown>,
  now = new Date()
): AnalyticsFilters {
  const result = analyticsDefaults(now);
  if (validAnalyticsDate(query.dateFrom)) result.dateFrom = query.dateFrom;
  if (validAnalyticsDate(query.dateTo)) result.dateTo = query.dateTo;
  result.comparison = query.comparison === 'year' ? 'year' : 'previous';
  result.granularity =
    query.step === 'day' || query.step === 'week' || query.step === 'month' ? query.step : 'auto';
  result.warehouseIds = ids(query.warehouses);
  result.brandIds = ids(query.brands, true);
  result.folderId = ids(query.category, true)[0];
  result.search = String(query.search || '').trim() || undefined;
  result.groupBy = query.group === 'brand' || query.group === 'category' ? query.group : 'product';
  result.noSales = query.noSales === 'true';
  result.noMinimumStock = query.noMinimumStock === 'true';
  const coverageDays = Number(query.coverageDays);
  result.coverageDays = Number.isInteger(coverageDays) && coverageDays >= 1 && coverageDays <= 365 ? coverageDays : 30;
  const page = Number(query.page);
  result.page = Number.isFinite(page) ? Math.max(1, Math.floor(page)) : 1;
  result.pageSize = [25, 50, 100].includes(Number(query.pageSize)) ? Number(query.pageSize) : 25;
  result.sortBy = ANALYTICS_SORT_FIELDS.includes(String(query.sort))
    ? String(query.sort)
    : 'revenue';
  result.sortDirection = query.direction === 'asc' ? 'asc' : 'desc';
  return result;
}

export function analyticsQueryFromFilters(
  filters: AnalyticsFilters
): Record<string, string | number | null> {
  return {
    dateFrom: filters.dateFrom,
    step: filters.granularity === 'auto' ? null : filters.granularity,
    dateTo: filters.dateTo,
    comparison: filters.comparison,
    noMinimumStock: filters.noMinimumStock ? 'true' : null,
    coverageDays: filters.coverageDays && filters.coverageDays !== 30 ? filters.coverageDays : null,
    warehouses: filters.warehouseIds.length ? filters.warehouseIds.join(',') : null,
    brands: filters.brandIds.length ? filters.brandIds.join(',') : null,
    category: filters.folderId ?? null,
    search: filters.search?.trim() || null,
    group: filters.groupBy,
    noSales: filters.noSales ? 'true' : null,
    page: filters.page === 1 ? null : filters.page,
    pageSize: filters.pageSize,
    sort: filters.sortBy,
    direction: filters.sortDirection
  };
}

export function familyAnalyticsFilters(
  filters: AnalyticsFilters,
  productIds: number[]
): AnalyticsFilters {
  return {
    ...filters,
    warehouseIds: [...filters.warehouseIds],
    brandIds: [],
    folderId: undefined,
    search: undefined,
    productIds: [...new Set(productIds)],
    groupBy: 'product',
    noSales: false,
    page: 1,
    pageSize: 100
  };
}

// Analytics counts each recorded catalogue unit as one piece, without package conversion.
export function analyticsQuantityValue(
  metrics: AnalyticsMetrics,
  field: 'soldQty' | 'returnedQty' | 'netQty' = 'netQty'
): number {
  const value = metrics[field];
  if (value !== null && value !== undefined) return value;
  return (metrics.quantities || []).reduce((sum, quantity) => sum + quantity[field], 0);
}

export function analyticsQuantityLabel(
  metrics: AnalyticsMetrics,
  field: 'soldQty' | 'returnedQty' | 'netQty' = 'netQty'
): string {
  return analyticsNumber(analyticsQuantityValue(metrics, field)) + ' шт.';
}

export function analyticsFolderOptions(
  folders: AnalyticsLookups['folders']
): Array<{ id: number; label: string; depth: number; path: string }> {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  return folders
    .map((folder) => {
      const ancestors = [folder.label];
      const visited = new Set([folder.id]);
      let parent = folder.parentId ? byId.get(folder.parentId) : undefined;
      while (parent && !visited.has(parent.id)) {
        visited.add(parent.id);
        ancestors.unshift(parent.label);
        parent = parent.parentId ? byId.get(parent.parentId) : undefined;
      }
      return {
        id: folder.id,
        label: folder.label,
        depth: ancestors.length - 1,
        path: ancestors.join(' / ')
      };
    })
    .sort((left, right) => left.path.localeCompare(right.path, 'uk'));
}

export function analyticsCategoryNavigation(
  folders: AnalyticsLookups['folders'],
  folderId?: number
): {
  parentId: number | undefined;
  parentLabel: string;
  children: AnalyticsLookups['folders'];
} {
  const current = folders.find((folder) => folder.id === folderId);
  const parent = folders.find((folder) => folder.id === current?.parentId);
  return {
    parentId: parent?.id,
    parentLabel: parent?.label || 'Все категории',
    children: folders.filter((folder) =>
      folderId === undefined
        ? folder.parentId === null
        : folder.parentId === folderId && folder.id !== folderId
    )
  };
}
